import React, { useState } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import KaModal from '../Common/KaModal';
import KaAlert from '../Common/KaAlert';
import SpinnerButton from '../Common/SpinnerButton';
import useDisplayMessage from '../Common/useDisplayMessage';
import useGetCustomerActivityReport from '../../requests/useGetCustomerActivityReport';
import { CustomerActivityReportDisplay, reportColumns } from './CustomerActivityReportDisplay';
import { KaSelect, toKaSelectOptions, isMultiValue, KaSelectOption } from '../Common/KaSelect';
import { unitList } from '../Common/Units';
import { Row, Col, Label } from 'reactstrap';
import useGetSitesNonAdmin from '../Requests/useGetSitesNonAdmin';
import useGetProducts from '../Requests/useGetProducts';
import useGetRecipes from '../Requests/useGetRecipes';
import useGetCustomers from '../../requests/useGetCustomers';
import ProductDto from '../Common/dtos/ProductDto';
import RecipeDto from '../Common/dtos/RecipeDto';
import { ItemType } from '../Orders/CreateEditOrderBlendItem';
import { customerDisplayNameFromDto } from '../../Utilities';
import useGetBranches from '../../requests/useGetBranches';
import MultiQueryView from '../Common/MultiQueryView';
import { useDownloadCustomerActivityReport } from '../../requests/useDownloadCustomerActivityReport';
import dayjs, { Dayjs } from 'dayjs';
import DateRange from '../Common/DateRange';

interface CustomerActivityReportModalProps {
  show: boolean;
  onHide: () => void;
}

export const CustomerActivityReportModal = ({ show, onHide }: CustomerActivityReportModalProps) => {
  const displayMessage = useDisplayMessage();
  const columnOptions = reportColumns.map(rc => ({ value: rc, label: rc }));

  const [selectedUnit, setSelectedUnit] = useState("Pound");
  const [selectedItem, setSelectedItem] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [selectedSite, setSelectedSite] = useState<string>("");
  const [selectedBranch, setSelectedBranch] = useState<string>("");
  const [selectedColumns, setSelectedColumns] = useState<KaSelectOption[]>(columnOptions);

  const dateFormat = 'MM/DD/YYYY';
  const toMydString = (date: Dayjs): string => date.format(dateFormat);
  const [dateFilterChecked, setDateFilterChecked] = useState(true);
  const [selectedDates, setSelectedDates] = useState({
    startDate: toMydString(dayjs().subtract(1, 'month')),
    endDate: toMydString(dayjs())
  });

  const getSites = useGetSitesNonAdmin();
  const getProducts = useGetProducts();
  const getRecipes = useGetRecipes();
  const getCustomers = useGetCustomers();
  const getBranches = useGetBranches();

  const queries = {
    sites: getSites,
    products: getProducts,
    recipes: getRecipes,
    customers: getCustomers,
    branches: getBranches,
  }

  const anyQueriesLoading = getSites.isLoading || getProducts.isLoading || getRecipes.isLoading || getCustomers.isLoading || getBranches.isLoading;

  const handleClose = () => {
    onHide();
    displayMessage.clear();
    setSelectedUnit("Pound");
    setSelectedItem("");
    setSelectedCustomer("");
    setSelectedSite("");
    setSelectedBranch("");
  };

  const params = {
    units: selectedUnit,
    customerId: selectedCustomer,
    itemId: selectedItem,
    branchId: selectedBranch,
    siteId: selectedSite,
    startDate: dateFilterChecked ? selectedDates.startDate : undefined,
    endDate: dateFilterChecked ? selectedDates.endDate : undefined,
  };

  const downloadQuery = useDownloadCustomerActivityReport(params);

  const reportQuery = useGetCustomerActivityReport(
    params,
    {
      onSuccess: (reportData) => {
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          displayMessage.fail('Unable to open report window. Please check your popup blocker settings.');
          return;
        }

        reportWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Customer Activity Report</title>
              <link href="${window.location.origin}/assets/vendor/bootstrap-icons/font/bootstrap-icons.css" rel="stylesheet">
              <link href="${window.location.origin}/assets/css/theme.min.css" rel="stylesheet">
              <link href="${window.location.origin}/assets/css/vendor.min.css" rel="stylesheet">
              <link href="${window.location.origin}/custom.css" rel="stylesheet">
            </head>
            <body>
              <div id="report-root"></div>
            </body>
          </html>
        `);

        Array.from(document.styleSheets).forEach(styleSheet => {
          if (reportWindow.document.head !== undefined) reportWindow.document.head.appendChild(styleSheet.ownerNode!.cloneNode(true))
        })

        reportWindow.document.close();

        const emotionCache = createCache({
          key: 'report-window',
          container: reportWindow.document.head
        });

        const container = reportWindow.document.getElementById('report-root')!;
        const root = ReactDOMClient.createRoot(container);
        root.render(
          <CacheProvider value={emotionCache}>
            <CustomerActivityReportDisplay reportData={reportData} downloadQuery={downloadQuery} columns={selectedColumns.map(c => c.value)} />
          </CacheProvider>
        );

        handleClose();
      },
      onError: (error) => {
        displayMessage.fail(error.message || 'Failed to generate report');
      }
    }
  );

  const unitOptions = toKaSelectOptions(unitList(), "value", "name", { sortByLabel: true });

  const listOfItems = (products: ProductDto[], recipes: RecipeDto[]) => [
    ...products.map(p => ({
      value: p.id,
      label: p.name,
      type: ItemType.Product
    })),
    ...recipes.map(r => ({
      value: r.id,
      label: r.name,
      type: ItemType.Recipe
    }))
  ];

  const items = listOfItems(getProducts.data ?? [], getRecipes.data ?? []);
  const itemOptions = toKaSelectOptions<KaSelectOption & { type: ItemType }, KaSelectOption & { type: ItemType }>(
    items,
    "value",
    "label",
    { sortByLabel: true }
  );

  const customerOptions = toKaSelectOptions(getCustomers.data ?? [],
    "id",
    "name",
    {
      labelFn: customerDisplayNameFromDto,
      sortByLabel: true
    });

  const siteOptions = toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true });

  const branchOptions = toKaSelectOptions(getBranches.data ?? [], "id", "name", { sortByLabel: true });

  const handleGenerateReport = () => {
    displayMessage.clear();
    reportQuery.query();
  };

  return (
    <KaModal
      show={show}
      onHide={handleClose}
      title="Customer Activity Report"
      body={
        <>
          <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
          <p>
            Generate a comprehensive report showing customer order transaction activity
            with product measurements and operational details.
          </p>
          <MultiQueryView
            queries={queries}
            renderData={_ => <>
              <Row className="mb-5">
                <Col md={6}>
                  <Label htmlFor="unit-selector" className="form-label">
                    Display Units
                  </Label>
                  <KaSelect
                    inputId="unit-selector"
                    options={unitOptions}
                    value={unitOptions.find(option => option.value === selectedUnit)}
                    onChange={(option) => setSelectedUnit(isMultiValue(option) ? "Pound" : (option?.value || "Pound"))}
                    placeholder="Select unit..."
                    isSearchable={false}
                  />
                </Col>
              </Row>
              <h5 className="mb-2">
                Show Columns
              </h5>
              <Row className="mb-5">
                <Col>
                  <KaSelect
                    placeholder="Select Columns"
                    options={columnOptions}
                    isClearable={true}
                    closeMenuOnSelect={false}
                    isMulti={true}
                    onChange={(valueObject) => {
                      if (isMultiValue(valueObject)) { setSelectedColumns(valueObject.map(c => c as KaSelectOption)) }
                    }}
                    value={selectedColumns} />
                </Col>
              </Row>
              <h5 className="mb-2">
                Filter report
              </h5>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="site-selector" className="form-label">
                    Site:
                  </Label>
                  <KaSelect
                    inputId="site-selector"
                    options={siteOptions}
                    value={siteOptions.find(option => option.value === selectedSite)}
                    onChange={(option) => setSelectedSite(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select site"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="customer-selector" className="form-label">
                    Customer:
                  </Label>
                  <KaSelect
                    inputId="customer-selector"
                    options={customerOptions}
                    value={customerOptions.find(option => option.value === selectedCustomer)}
                    onChange={(option) => setSelectedCustomer(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select customer"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="item-selector" className="form-label">
                    Product/Recipe:
                  </Label>
                  <KaSelect
                    inputId="item-selector"
                    options={itemOptions}
                    value={itemOptions.find(option => option.value === selectedItem)}
                    onChange={(option) => setSelectedItem(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select product or recipe"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col>
                  <Label htmlFor="branch-selector" className="form-label">
                    Branch:
                  </Label>
                  <KaSelect
                    inputId="branch-selector"
                    options={branchOptions}
                    value={branchOptions.find(option => option.value === selectedBranch)}
                    onChange={(option) => setSelectedBranch(isMultiValue(option) ? "" : (option?.value || ""))}
                    placeholder="Select branch"
                    isClearable={true}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col className="form-check ms-3">
                  <input
                    type="checkbox"
                    id="toggleDateFilter"
                    className="form-check-input"
                    checked={dateFilterChecked}
                    onChange={(e) => setDateFilterChecked(e.target.checked)} />
                  <label htmlFor="toggleDateFilter" className="form-check-label">
                    Filter by date range
                  </label>
                </Col>
              </Row>
              {dateFilterChecked && (
                <Row>
                  <Col>
                    <DateRange
                      className="row mt-3"
                      startDate={selectedDates.startDate}
                      endDate={selectedDates.endDate}
                      rangeUpdated={(startDate, endDate) => setSelectedDates({ startDate, endDate })}
                      dateFormat={dateFormat} />
                  </Col>
                </Row>
            )}
            </>} />
        </>
      }
      footer={!anyQueriesLoading && <>
        <SpinnerButton
          spinning={false}
          onClick={downloadQuery}>
          Download CSV
        </SpinnerButton>
        <SpinnerButton
          spinning={reportQuery.isLoading}
          onClick={handleGenerateReport}>
          View in New Tab
        </SpinnerButton>
      </>
      }
    />
  );
};